import EmptyList from '@ifca-root/react-component/src/components/CardList/EmptyList';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { Divider, List, ListItem, ListItemText } from '@material-ui/core';
import { useGetStockTransactionsQuery, StockTransactionType } from 'generated/graphql';
import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { StockTransactionItemsDialog } from '../StockTransaction/StockTransactionItemsDialog';
import CachedIcon from '@material-ui/icons/Cached';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';

interface StockTransactionTabProps {
	outletID: string;
	startDate: Date | null;
	endDate: Date | null;
	searchQuery: string;
}

export const StockTransactionTab: React.FC<StockTransactionTabProps> = ({
	outletID,
	startDate,
	endDate,
	searchQuery,
}) => {
	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedTransactionID, setSelectedTransactionID] = useState<string | null>(null);
	const [selectedTransactionType, setSelectedTransactionType] = useState<StockTransactionType | null>(null);

	const { data, loading } = useGetStockTransactionsQuery({
		fetchPolicy: 'network-only',
		variables: {
			outletID: outletID,
			startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
			endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
		},
	});

	const handleItemCountClick = (transactionID: string, transactionType: StockTransactionType) => {
		setSelectedTransactionID(transactionID);
		setSelectedTransactionType(transactionType);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setSelectedTransactionID(null);
		setSelectedTransactionType(null);
	};

	// Filter by search query
	const filteredItems = useMemo(() => {
		if (!data?.getStockTransactions) return [];

		let items = data.getStockTransactions;

		// Filter by search query
		if (searchQuery) {
			const query = searchQuery.toLowerCase();
			items = items.filter((item: any) =>
				item.docRef?.toLowerCase().includes(query) ||
				item.docNo?.toLowerCase().includes(query) ||
				item.description?.toLowerCase().includes(query)
			);
		}

		// Items are already sorted by trxDate DESC from backend
		return items;
	}, [data, searchQuery]);

	const getTransactionTypeLabel = (type: string) => {
		switch (type) {
			case 'STOCK_IN':
				return 'Stock In';
			case 'STOCK_OUT':
				return 'Stock Out';
			case 'STOCK_ADJUSTMENT':
				return 'Stock Adjustment';
			default:
				return type;
		}
	};

	const getTransactionTypeIcon = (type: StockTransactionType) => {
		switch (type) {
			case StockTransactionType.StockIn:
				return  <TrendingDownIcon style={{ fontSize: '16px', color: '#4caf50', marginRight: '4px' }} />;
				
			case StockTransactionType.StockOut:
				return <TrendingUpIcon style={{ fontSize: '16px', color:'#f44336', marginRight: '4px' }} />;
			case StockTransactionType.StockAdjustment:
				return <CachedIcon style={{ fontSize: '16px', color: '#9e9e9e', marginRight: '4px' }} />;
			default:
				return null;
		}
	};

	if (loading) {
		return <Loading />;
	}

	return (
		<>
			<List className="core-list">
				{filteredItems.length === 0 ? (
					<EmptyList
						title="No stock transactions found"
						subtitle="Stock transactions will appear here"
					/>
				) : (
					filteredItems.map((item: any, index: number) => (
						<React.Fragment key={item.ID}>
							<ListItem>
								<ListItemText
									primary={
										<span className="flex-space desc">
											{format(new Date(item.trxDate), 'dd/MM/yyyy')}
											<span className="xsTitle" style={{ marginLeft: '8px' }}>{item.docNo && `  ${item.docNo}`}</span>
											{/* <span className="xsTitle right-info">{getTransactionTypeLabel(item.transactionType)}</span> */}
											<span
												className="desc right-info"
												style={{
													cursor: item.itemCount > 0 ? 'pointer' : 'default',
													color: item.itemCount > 0 ? '#1976d2' : 'inherit',
													textDecoration: item.itemCount > 0 ? 'underline' : 'none',
													display: 'flex',
													alignItems: 'center',
												}}
												onClick={() => {
													if (item.itemCount > 0) {
														handleItemCountClick(item.ID, item.transactionType as StockTransactionType);
													}
												}}
											>
												{getTransactionTypeIcon(item.transactionType as StockTransactionType)}
												{item.itemCount} {item.itemCount === 1 ? 'item' : 'items'}
											</span>
										</span>
									}
									secondary={
										<>
											<div className="desc">
												{item.docRef && `${item.docRef} | `}
												{item.transactionType === 'STOCK_OUT' && item.toWarehouseName && ` | Transfer to: ${item.toWarehouseName}`}
											</div>
											{item.description && (
												<div className="desc" style={{ marginLeft: '4px', fontStyle: 'italic' }}>
													{item.description}
												</div>
											)}
										</>
									}
								/>
							</ListItem>
							{index < filteredItems.length - 1 && (
								<Divider component="li" />
							)}
						</React.Fragment>
					))
				)}
			</List>
			<StockTransactionItemsDialog
				open={dialogOpen}
				onClose={handleCloseDialog}
				transactionID={selectedTransactionID}
				transactionType={selectedTransactionType}
			/>
		</>
	);
};

